import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import {
  Bold,
  Clipboard,
  ClipboardPipeline,
  Enter,
  InlineEditor,
  Italic,
  Paragraph,
  Plugin,
  SelectAll,
  ShiftEnter,
  Typing,
  Widget,
  toWidget,
  viewToModelPositionOutsideModelElement,
  type ClipboardOutputTransformationEvent,
  type ModelDocumentFragment,
  type ModelElement,
  type ModelWriter,
  type ViewDocumentClipboardInputEvent,
} from 'ckeditor5';
import type { NoticeRun } from './notice-fidelity.models';
import type { NoticeReferenceSelection } from './notice-reference-commands';
type InlineReferenceSelection = Omit<NoticeReferenceSelection, 'blockId'>;
import {
  NoticeInlineClipboard,
  normalizeNoticeRuns,
  noticePlainTextSegments,
  noticeRunsToSegments,
  noticeSegmentsToRuns,
  type NoticeInlineSegment,
} from './notice-inline-runs';

const clipboard = new NoticeInlineClipboard();
const clipboardMime = 'application/x-phms-notice-fragment';

class NoticeReferences extends Plugin {
  static get requires() {
    return [Widget];
  }

  init(): void {
    const editor = this.editor;
    editor.model.schema.register('noticeReference', {
      inheritAllFrom: '$inlineObject',
      allowAttributes: [
        'referenceKind',
        'code',
        'format',
        'bold',
        'italic',
        'label',
        'known',
      ],
    });
    editor.model.schema.extend('softBreak', {
      allowAttributes: ['bold', 'italic'],
    });
    // No reference upcast is registered: pasted HTML cannot create governed references.
    editor.conversion.for('dataDowncast').elementToElement({
      model: 'noticeReference',
      view: (model, { writer }) => {
        const element = writer.createContainerElement('span', {
          'data-notice-reference': String(model.getAttribute('referenceKind')),
          'data-notice-code': String(model.getAttribute('code')),
        });
        writer.insert(
          writer.createPositionAt(element, 0),
          writer.createText(
            `{{${String(model.getAttribute('referenceKind'))}:${String(model.getAttribute('code'))}}}`,
          ),
        );
        return element;
      },
    });
    editor.conversion.for('editingDowncast').elementToElement({
      model: {
        name: 'noticeReference',
        attributes: ['label', 'known', 'format', 'bold', 'italic'],
      },
      view: (model, { writer }) => {
        const kind =
          model.getAttribute('referenceKind') === 'field' ? 'Data' : 'Term';
        const label = String(model.getAttribute('label'));
        const code = String(model.getAttribute('code'));
        const format = model.getAttribute('format');
        const element = writer.createContainerElement('span', {
          class: `notice-reference notice-reference--${String(model.getAttribute('referenceKind'))}${model.getAttribute('known') ? '' : ' notice-reference--unknown'}`,
          title: `${kind}: ${code}${format ? ` · ${String(format)}` : ''}`,
          style: `${model.getAttribute('bold') ? 'font-weight:700;' : ''}${model.getAttribute('italic') ? 'font-style:italic;' : ''}`,
          'data-reference-kind': String(model.getAttribute('referenceKind')),
          'data-reference-code': code,
        });
        writer.insert(
          writer.createPositionAt(element, 0),
          writer.createText(`${kind} · ${label}`),
        );
        return toWidget(element, writer, {
          label: `${kind} reference ${label}, ${code}`,
        });
      },
    });
    editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(editor.model, (viewElement) =>
        viewElement.hasClass('notice-reference'),
      ),
    );
  }
}

@Component({
  selector: 'app-notice-inline-editor',
  standalone: true,
  templateUrl: './notice-inline-editor.component.html',
  styleUrl: './notice-inline-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NoticeInlineEditorComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() runs: readonly NoticeRun[] = [];
  @Input() fields: readonly { code: string; label: string }[] = [];
  @Input() terms: readonly { code: string; label: string }[] = [];
  @Input() readOnly = false;
  @Input() ariaLabel = 'Notice text';
  @Input() selectedReferenceIndex: number | null = null;
  @Output() readonly runsChange = new EventEmitter<NoticeRun[]>();
  @Output() readonly activated = new EventEmitter<void>();
  @Output() readonly referenceSelected =
    new EventEmitter<InlineReferenceSelection | null>();
  @Output() readonly historyCommand = new EventEmitter<'undo' | 'redo'>();
  @Output() readonly breakHistoryGroup = new EventEmitter<void>();
  @ViewChild('surface', { static: true })
  private surface!: ElementRef<HTMLElement>;

  readonly ready = signal(false);
  readonly editorError = signal<string | null>(null);
  private editor: InlineEditor | null = null;
  private destroyed = false;
  private synchronizing = false;
  private modelSignature = '';
  private catalogSignature = '';
  private starting: Promise<void> | null = null;
  private readonly historyKeydown = (event: KeyboardEvent): void => {
    if (!(event.ctrlKey || event.metaKey) || event.altKey || this.readOnly)
      return;
    const key = event.key.toLowerCase();
    if (key !== 'z' && key !== 'y') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.historyCommand.emit(key === 'y' || event.shiftKey ? 'redo' : 'undo');
  };

  ngAfterViewInit(): void {
    this.surface.nativeElement.addEventListener(
      'keydown',
      this.historyKeydown,
      true,
    );
  }

  async startEditing(): Promise<void> {
    if (this.readOnly) return;
    this.activated.emit();
    await this.focus();
  }

  private ensureEditor(): Promise<void> {
    if (!this.starting) this.starting = this.createEditor();
    return this.starting;
  }

  private async createEditor(): Promise<void> {
    let createdEditor: InlineEditor | null = null;
    try {
      const editor = await InlineEditor.create(this.surface.nativeElement, {
        licenseKey: 'GPL',
        // The parent owns document history, so this deliberately excludes CKEditor's Undo plugin.
        plugins: [
          Typing,
          Clipboard,
          Enter,
          ShiftEnter,
          SelectAll,
          Paragraph,
          Bold,
          Italic,
          NoticeReferences,
        ],
        toolbar: { items: ['bold', 'italic'], shouldNotGroupWhenFull: true },
        placeholder: 'Type notice text…',
      });
      createdEditor = editor;
      if (this.destroyed) {
        await editor.destroy();
        return;
      }
      this.editor = editor;
      this.synchronizeRuns(true);
      this.synchronizeReadOnly();
      this.synchronizeLabel();
      editor.model.document.on('change:data', () => {
        if (this.synchronizing || this.readOnly) return;
        const runs = this.readRuns();
        const signature = JSON.stringify(runs);
        if (signature === this.modelSignature) return;
        this.modelSignature = signature;
        this.runsChange.emit(runs);
      });
      this.bindHistoryBoundaries(editor);
      editor.model.document.selection.on('change:range', () => {
        if (this.synchronizing || this.readOnly || this.destroyed) return;
        const selected = editor.model.document.selection.getSelectedElement();
        const references = this.referenceElements();
        const referenceIndex = selected ? references.indexOf(selected) : -1;
        this.referenceSelected.emit(
          referenceIndex < 0
            ? null
            : this.referenceIdentity(
                references[referenceIndex],
                referenceIndex,
              ),
        );
      });
      editor.plugins
        .get(ClipboardPipeline)
        .on<ClipboardOutputTransformationEvent>(
          'outputTransformation',
          (_event, data) => {
            const key = crypto.randomUUID();
            clipboard.remember(
              key,
              noticeSegmentsToRuns(this.readSegments(data.content)),
            );
            data.dataTransfer.setData(clipboardMime, key);
          },
          { priority: 'high' },
        );
      editor.editing.view.document.on<ViewDocumentClipboardInputEvent>(
        'clipboardInput',
        (event, data) => {
          event.stop();
          // ClipboardObserver has already prevented the original paste/drop.
          // This derived payload is plain data, not ViewDocumentDomEventData.
          if (this.readOnly) return;
          const ownFragment = clipboard.read(
            data.dataTransfer.getData(clipboardMime),
          );
          const text = data.dataTransfer.getData('text/plain');
          if (!ownFragment && !text) return;
          // Own issued fragments preserve chips; external content uses literal text only.
          this.breakHistoryGroup.emit();
          try {
            editor.model.change((writer) => {
              const selection = editor.model.document.selection;
              const fragment = writer.createDocumentFragment();
              this.appendSegments(
                writer,
                fragment,
                ownFragment
                  ? noticeRunsToSegments(ownFragment)
                  : noticePlainTextSegments(
                      text,
                      !!selection.getAttribute('bold'),
                      !!selection.getAttribute('italic'),
                    ),
              );
              editor.model.insertContent(fragment);
            });
          } finally {
            this.breakHistoryGroup.emit();
          }
        },
        { priority: 'highest' },
      );
      this.ready.set(true);
    } catch {
      this.editor = null;
      if (createdEditor && !this.destroyed) void createdEditor.destroy();
      if (!this.destroyed)
        this.editorError.set(
          'Text editing could not start. Use Fields to edit this block.',
        );
    }
  }

  private bindHistoryBoundaries(editor: InlineEditor): void {
    for (const commandName of ['bold', 'italic']) {
      const command = editor.commands.get(commandName);
      // Attribute commands execute model changes synchronously between these events.
      command?.on('execute', () => this.breakHistoryGroup.emit(), {
        priority: 'high',
      });
      command?.on('execute', () => this.breakHistoryGroup.emit(), {
        priority: 'lowest',
      });
    }
    // The clipboard pipeline deletes cut content at low priority. Plain copying
    // creates no document revision and therefore does not break a typing group.
    editor.editing.view.document.on(
      'cut',
      () => this.breakHistoryGroup.emit(),
      {
        priority: 'high',
      },
    );
    editor.editing.view.document.on(
      'cut',
      () => this.breakHistoryGroup.emit(),
      {
        priority: 'lowest',
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.editor) return;
    this.synchronizeRuns(!!(changes['fields'] || changes['terms']));
    this.synchronizeReadOnly();
    this.synchronizeLabel();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.surface?.nativeElement.removeEventListener(
      'keydown',
      this.historyKeydown,
      true,
    );
    const editor = this.editor;
    this.editor = null;
    clipboard.clear();
    if (editor) void editor.destroy();
  }

  async focus(): Promise<boolean> {
    if (this.readOnly || this.destroyed) return false;
    await this.ensureEditor();
    if (!this.editor || this.readOnly) return false;
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );
    if (!this.editor || this.readOnly || this.destroyed) return false;
    this.editor.editing.view.focus();
    return true;
  }

  async insertToken(
    kind: 'field' | 'term',
    code: string,
    format?: string,
    canInsert: () => boolean = () => true,
  ): Promise<boolean> {
    const catalog = kind === 'field' ? this.fields : this.terms;
    if (
      this.readOnly ||
      this.destroyed ||
      !canInsert() ||
      !catalog.some((item) => item.code === code)
    )
      return false;
    await this.ensureEditor();
    const editor = this.editor;
    if (
      !editor ||
      this.readOnly ||
      this.destroyed ||
      !canInsert() ||
      !(kind === 'field' ? this.fields : this.terms).some(
        (item) => item.code === code,
      )
    )
      return false;
    this.activated.emit();
    if (!canInsert()) return false;
    editor.model.change((writer) => {
      const selection = editor.model.document.selection;
      const element = this.createReference(writer, {
        kind: 'reference',
        referenceKind: kind,
        code,
        bold: !!selection.getAttribute('bold'),
        italic: !!selection.getAttribute('italic'),
        ...(format === undefined ? {} : { format }),
      });
      editor.model.insertObject(element, null, null, { setSelection: 'after' });
    });
    editor.editing.view.focus();
    return true;
  }

  inspectPreviewReference(runIndex: number, event: Event): void {
    event.stopPropagation();
    if (this.readOnly || this.destroyed) return;
    const run = this.runs[runIndex];
    if (!run || run.kind === 'literal') return;
    this.activated.emit();
    this.referenceSelected.emit({
      referenceIndex: this.referenceOrdinal(runIndex),
      kind: run.kind,
      code: run.value,
      ...(run.format === undefined ? {} : { format: run.format }),
    });
  }
  referenceOrdinal(runIndex: number): number {
    return this.runs.slice(0, runIndex).filter((run) => run.kind !== 'literal')
      .length;
  }
  async focusReference(selection: InlineReferenceSelection): Promise<boolean> {
    if (this.readOnly || this.destroyed) return false;
    await this.ensureEditor();
    const editor = this.editor;
    const element = this.referenceElements()[selection.referenceIndex];
    if (
      !editor ||
      !element ||
      this.readOnly ||
      this.destroyed ||
      element.getAttribute('referenceKind') !== selection.kind ||
      element.getAttribute('code') !== selection.code ||
      element.getAttribute('format') !== selection.format
    )
      return false;
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );
    if (
      !this.editor ||
      this.editor !== editor ||
      this.readOnly ||
      this.destroyed
    )
      return false;
    const current = this.referenceElements()[selection.referenceIndex];
    if (
      !current ||
      current.getAttribute('referenceKind') !== selection.kind ||
      current.getAttribute('code') !== selection.code ||
      current.getAttribute('format') !== selection.format
    )
      return false;
    // Restore DOM focus first: CKEditor can otherwise restore the previous caret
    // while focusing and overwrite the programmatic widget selection.
    editor.editing.view.focus();
    editor.model.change((writer) => writer.setSelection(current, 'on'));
    return true;
  }
  private referenceElements(): ModelElement[] {
    const root = this.editor?.model.document.getRoot();
    if (!root) return [];
    const result: ModelElement[] = [];
    for (const paragraph of root.getChildren()) {
      const children = paragraph.is('element', 'paragraph')
        ? [...paragraph.getChildren()]
        : [paragraph];
      for (const node of children)
        if (node.is('element', 'noticeReference')) result.push(node);
    }
    return result;
  }
  private referenceIdentity(
    element: ModelElement,
    referenceIndex: number,
  ): InlineReferenceSelection {
    const format = element.getAttribute('format');
    return {
      referenceIndex,
      kind: element.getAttribute('referenceKind') as 'field' | 'term',
      code: String(element.getAttribute('code')),
      ...(format === undefined ? {} : { format: String(format) }),
    };
  }
  referenceLabel(run: NoticeRun): string {
    const entries = run.kind === 'field' ? this.fields : this.terms;
    return (
      entries.find((entry) => entry.code === run.value)?.label ??
      `Unknown: ${run.value || '(empty reference)'}`
    );
  }

  knownReference(run: NoticeRun): boolean {
    return (run.kind === 'field' ? this.fields : this.terms).some(
      (entry) => entry.code === run.value,
    );
  }

  private synchronizeReadOnly(): void {
    if (this.readOnly) {
      clipboard.clear();
      this.editor?.enableReadOnlyMode('notice-permission');
    } else this.editor?.disableReadOnlyMode('notice-permission');
  }

  private synchronizeLabel(): void {
    const editor = this.editor;
    if (!editor) return;
    const root = editor.editing.view.document.getRoot();
    if (root)
      editor.editing.view.change((writer) =>
        writer.setAttribute('aria-label', this.ariaLabel, root),
      );
  }

  private synchronizeRuns(force = false): void {
    const editor = this.editor;
    if (!editor) return;
    const signature = JSON.stringify(normalizeNoticeRuns(this.runs));
    const catalogSignature = JSON.stringify([this.fields, this.terms]);
    if (
      signature === this.modelSignature &&
      (!force || catalogSignature === this.catalogSignature)
    )
      return;
    this.synchronizing = true;
    try {
      editor.model.change((writer) => {
        const root = editor.model.document.getRoot();
        if (!root) return;
        const first = editor.model.document.selection.getFirstPosition();
        const offset = first?.parent.is('element', 'paragraph')
          ? first.offset
          : 0;
        writer.remove(writer.createRangeIn(root));
        const paragraph = writer.createElement('paragraph');
        writer.append(paragraph, root);
        this.appendSegments(writer, paragraph, noticeRunsToSegments(this.runs));
        writer.setSelection(paragraph, Math.min(offset, paragraph.maxOffset));
      });
      this.modelSignature = signature;
      this.catalogSignature = catalogSignature;
    } finally {
      this.synchronizing = false;
    }
  }

  private appendSegments(
    writer: ModelWriter,
    parent: Parameters<ModelWriter['append']>[1],
    segments: readonly NoticeInlineSegment[],
  ): void {
    for (const segment of segments) {
      const attributes = {
        ...(segment.bold ? { bold: true } : {}),
        ...(segment.italic ? { italic: true } : {}),
      };
      if (segment.kind === 'text')
        writer.appendText(segment.text, attributes, parent);
      else if (segment.kind === 'break')
        writer.appendElement('softBreak', attributes, parent);
      else writer.append(this.createReference(writer, segment), parent);
    }
  }

  private createReference(
    writer: ModelWriter,
    segment: Extract<NoticeInlineSegment, { kind: 'reference' }>,
  ): ModelElement {
    const catalog =
      segment.referenceKind === 'field' ? this.fields : this.terms;
    const entry = catalog.find((item) => item.code === segment.code);
    return writer.createElement('noticeReference', {
      referenceKind: segment.referenceKind,
      code: segment.code,
      bold: segment.bold,
      italic: segment.italic,
      label: entry?.label ?? `Unknown: ${segment.code || '(empty reference)'}`,
      known: !!entry,
      ...(segment.format === undefined ? {} : { format: segment.format }),
    });
  }

  private readRuns(): NoticeRun[] {
    const root = this.editor?.model.document.getRoot();
    if (!root) return [];
    return noticeSegmentsToRuns(this.readSegments(root));
  }

  private readSegments(
    root: ModelElement | ModelDocumentFragment,
  ): NoticeInlineSegment[] {
    const segments: NoticeInlineSegment[] = [];
    let paragraphIndex = 0;
    for (const paragraph of root.getChildren()) {
      const children = paragraph.is('element', 'paragraph')
        ? [...paragraph.getChildren()]
        : [paragraph];
      if (paragraph.is('element', 'paragraph') && paragraphIndex++ > 0)
        segments.push({ kind: 'break', bold: false, italic: false });
      for (const node of children) {
        const emphasis = {
          bold: !!node.getAttribute('bold'),
          italic: !!node.getAttribute('italic'),
        };
        if (node.is('$text'))
          segments.push({ kind: 'text', text: node.data, ...emphasis });
        else if (node.is('element', 'softBreak'))
          segments.push({ kind: 'break', ...emphasis });
        else if (node.is('element', 'noticeReference')) {
          const format = node.getAttribute('format');
          segments.push({
            kind: 'reference',
            referenceKind: node.getAttribute('referenceKind') as
              | 'field'
              | 'term',
            code: String(node.getAttribute('code')),
            ...emphasis,
            ...(format === undefined ? {} : { format: String(format) }),
          });
        }
      }
    }
    return segments;
  }
}
